import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../hooks/useBranding';
import ChangePasswordModal from './ChangePasswordModal';
import PoweredByBlockBoard from './PoweredByBlockBoard';
import VerifyEmailBanner from './VerifyEmailBanner';
import PaymentFailedBanner from './PaymentFailedBanner';
import logoMain from '../assets/logo-main.png';
import logoSymbol from '../assets/logo-symbol.png';
import { 
  HomeIcon, 
  DocumentIcon, 
  CalendarIcon, 
  ExclamationTriangleIcon, 
  CreditCardIcon, 
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  CogIcon,
  Bars3Icon,
  XMarkIcon,
  UsersIcon,
  BuildingOffice2Icon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/solid';

type MenuItem = { name: string; path: string; icon: React.ComponentType<{ className?: string }> };
type MenuSection = { section?: string; items: MenuItem[] };

const getMenuSections = (userRole: string, prefix: string = ''): MenuSection[] => {
  const p = (path: string) => `${prefix}${path}`;

  if (userRole === 'ADMIN') {
    return [
      {
        section: 'Overview',
        items: [
          { name: 'Dashboard', path: p('/dashboard'), icon: HomeIcon },
          { name: 'Payments', path: p('/payments'), icon: CreditCardIcon },
        ],
      },
      {
        section: 'Community',
        items: [
          { name: 'Users', path: p('/users'), icon: UsersIcon },
          { name: 'Issues', path: p('/issues'), icon: ExclamationTriangleIcon },
          { name: 'Events', path: p('/events'), icon: CalendarIcon },
          { name: 'Documents', path: p('/documents'), icon: DocumentIcon },
        ],
      },
      {
        section: 'Setup',
        items: [
          { name: 'Addresses', path: p('/properties'), icon: BuildingOffice2Icon },
          { name: 'Bulk Import', path: p('/bulk-import'), icon: ArrowUpTrayIcon },
          { name: 'Vendors', path: p('/vendors'), icon: UserGroupIcon },
        ],
      },
      {
        section: 'Account',
        items: [
          { name: 'Subscription', path: p('/subscription'), icon: CreditCardIcon },
          { name: 'Settings', path: p('/settings'), icon: CogIcon },
        ],
      },
    ];
  }

  if (userRole === 'RESIDENT') {
    return [
      {
        items: [
          { name: 'Home', path: '/dashboard', icon: HomeIcon },
          { name: 'My Payments', path: '/payments', icon: CreditCardIcon },
          { name: 'Events', path: '/events', icon: CalendarIcon },
          { name: 'Maintenance Requests', path: '/issues', icon: ExclamationTriangleIcon },
          { name: 'Documents', path: '/documents', icon: DocumentIcon },
          { name: 'Services', path: '/vendors', icon: UserGroupIcon },
        ],
      },
    ];
  }

  if (userRole === 'VENDOR') {
    return [
      {
        items: [
          { name: 'Dashboard', path: '/vendor-dashboard', icon: HomeIcon },
          { name: 'Showcase', path: '/vendor-showcase', icon: UserGroupIcon },
        ],
      },
    ];
  }

  return [
    {
      items: [
        { name: 'Dashboard', path: p('/dashboard'), icon: HomeIcon },
        { name: 'Documents', path: p('/documents'), icon: DocumentIcon },
        { name: 'Events', path: p('/events'), icon: CalendarIcon },
        { name: 'Issues', path: p('/issues'), icon: ExclamationTriangleIcon },
      ],
    },
  ];
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { groupSlug } = useParams<{ groupSlug?: string }>();
  const { branding } = useBranding();

  // Prefix all admin nav links with the group slug when inside a slug-based route
  const prefix = groupSlug ? `/${groupSlug}` : '';
  const menuSections = getMenuSections(user?.role || 'RESIDENT', prefix);
  const menuItems = menuSections.flatMap((s) => s.items);

  // Check if user must reset password
  useEffect(() => {
    if (user?.mustResetPassword) {
      setShowPasswordModal(true);
    }
  }, [user?.mustResetPassword]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="relative h-screen bg-[#0f0f10] text-white overflow-hidden">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={toggleMobileMenu}
        />
      )}

      {/* Floating Curved Sidebar */}
      <div 
        className={`
          fixed z-50 h-full transition-all duration-500 ease-out
          ${isSidebarOpen ? 'w-72' : 'w-20'} 
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          glass-sidebar m-4 rounded-3xl overflow-y-auto
        `}
      >
        <div className="flex flex-col h-full p-6 pb-24 lg:pb-6">
          {/* Logo Area */}
          <div className="flex items-center justify-between mb-8">
            {/* Full logo — visible when sidebar is open */}
            <div className={`
              transition-all duration-300
              ${isSidebarOpen ? 'opacity-100 flex-1' : 'opacity-0 w-0 overflow-hidden'}
            `}>
              {branding?.logoUrl ? (
                <div className="flex flex-col items-center">
                  <img
                    src={branding.logoUrl}
                    alt={branding.name || 'Property logo'}
                    title={branding.name || ''}
                    className="max-h-20 w-auto max-w-full object-contain"
                  />
                </div>
              ) : branding?.name ? (
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-[#22d3ee] to-[#38bdf8] bg-clip-text text-transparent leading-tight">
                    {branding.name}
                  </h1>
                  <p className="text-sm text-white/60 mt-0.5">Property Management</p>
                </div>
              ) : (
                <div>
                  <img src={logoMain} alt="BlockBoard" className="h-9 w-auto object-contain" />
                  <p className="text-sm text-white/60 mt-1.5">Property Management</p>
                </div>
              )}
            </div>

            {/* Small icon — visible when sidebar is collapsed */}
            <div className={`
              transition-all duration-300 flex justify-center
              ${isSidebarOpen ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-full'}
            `}>
              {branding?.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt={branding.name || 'Logo'}
                  className="w-9 h-9 object-contain rounded-lg"
                />
              ) : (
                <img src={logoSymbol} alt="BlockBoard" className="w-9 h-9 object-contain" />
              )}
            </div>
            
            {/* Mobile Close Button */}
            <button 
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-xl hover:bg-white/10 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 space-y-4">
            {menuSections.map((section, sectionIdx) => (
              <div key={section.section || `section-${sectionIdx}`} className="space-y-1">
                {section.section && isSidebarOpen && (
                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-white/40 font-semibold">
                    {section.section}
                  </div>
                )}
                {section.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    title={!isSidebarOpen ? item.name : undefined}
                    className={`
                      ${location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                        ? 'nav-item-active'
                        : 'nav-item'
                      }
                    `}
                  >
                    <item.icon className="w-5 h-5 text-white/80 group-hover:text-white transition-colors" />
                    <span
                      className={`
                        ml-3 font-medium transition-all duration-300
                        ${isSidebarOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 lg:w-0 lg:overflow-hidden'}
                      `}
                    >
                      {item.name}
                    </span>
                  </Link>
                ))}
              </div>
            ))}
          </nav>

          {/* User Profile Section — click to open profile */}
          <Link
            to="/profile"
            title="My Profile"
            className={`
              mt-6 p-4 rounded-2xl bg-white/5 border border-white/10
              hover:bg-white/10 hover:border-white/20 transition-all duration-300
              block
              ${isSidebarOpen ? 'opacity-100' : 'opacity-0 lg:opacity-100'}
            `}
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#22d3ee] to-[#38bdf8] flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-white">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className={`
                transition-all duration-300 min-w-0
                ${isSidebarOpen ? 'opacity-100' : 'opacity-0 lg:w-0 lg:overflow-hidden'}
              `}>
                <p className="text-sm font-medium text-white truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-white/60 capitalize">{user?.role?.toLowerCase() || 'Member'}</p>
              </div>
            </div>
          </Link>

          {/* Logout Button */}
          <div className="mt-4">
            <button 
              onClick={handleLogout}
              className="
                w-full flex items-center justify-center 
                btn-secondary group
              "
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5 group-hover:rotate-12 transition-transform duration-200" />
              <span className={`
                ml-3 transition-all duration-300
                ${isSidebarOpen ? 'opacity-100' : 'opacity-0 lg:w-0 lg:overflow-hidden'}
              `}>
                Logout
              </span>
            </button>
          </div>
        </div>

        {/* Desktop Sidebar Toggle */}
        <button 
          onClick={toggleSidebar} 
          className="
            hidden lg:flex absolute top-1/2 -right-4 transform -translate-y-1/2 
            w-8 h-8 rounded-full neon-accent
            items-center justify-center
            hover:scale-110 transition-all duration-200
            shadow-lg shadow-[#22d3ee]/25
          "
        >
          <div className={`transform transition-transform duration-300 ${isSidebarOpen ? 'rotate-180' : ''}`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>
      </div>

      {/* Main Content Area */}
      <div 
        className={`
          absolute top-0 right-0 bottom-0 overflow-y-auto transition-all duration-500 ease-out
          ${isSidebarOpen ? 'left-0 lg:left-80' : 'left-0 lg:left-28'}
          bg-[#0f0f10] p-4 lg:p-6
          flex flex-col min-h-screen
        `}
      >
        {/* Mobile Top Bar */}
        <div className="lg:hidden flex items-center justify-between mb-6 p-4 glass-card">
          <button 
            onClick={toggleMobileMenu}
            className="p-2 rounded-xl hover:bg-white/10 transition-colors"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold">
            {menuItems.find(item => item.path === location.pathname)?.name || 'Dashboard'}
          </h1>
          <div className="w-8 h-8 rounded-xl bg-gradient-to-r from-[#22d3ee] to-[#38bdf8] flex items-center justify-center">
            <span className="text-sm font-semibold text-white">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
        </div>

        {/* Desktop Top Navigation */}
        <header className="
          hidden lg:flex mb-8 p-6 
          glass-card
          justify-between items-center
        ">
          <div>
            <h1 className="text-3xl font-bold text-white">
              {menuItems.find(item => item.path === location.pathname)?.name || 'Dashboard'}
            </h1>
            <p className="text-white/60 mt-1">
              Welcome back, {user?.name || 'User'}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Switch Property Groups - Only for Admins */}
            {user?.role === 'ADMIN' && (
              <button 
                onClick={() => navigate('/select-property-group')}
                className="relative p-3 rounded-xl hover:bg-white/10 transition-colors group"
                title="Switch Property Groups"
              >
                <svg className="w-6 h-6 text-white/80 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m-2 0H7m5 0v-9a2 2 0 00-2-2H9a2 2 0 00-2 2v9m2 0h2m0 0h2" />
                </svg>
              </button>
            )}
            
            {/* User Avatar — click to open profile */}
            <Link
              to="/profile"
              title="My Profile"
              className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#22d3ee] to-[#38bdf8] flex items-center justify-center shadow-lg shadow-[#22d3ee]/25 hover:opacity-80 transition-opacity"
            >
              <span className="text-lg font-semibold text-white">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </Link>
          </div>
        </header>

        {/* Page Content with Animations */}
        {user?.role === 'ADMIN' && user?.isEmailVerified === false && (
          <VerifyEmailBanner email={user.email} />
        )}
        <main className="animate-fade-in flex-1">
          {user?.role === 'ADMIN' ? (
            <PaymentFailedBanner>{children}</PaymentFailedBanner>
          ) : (
            children
          )}
        </main>
        <PoweredByBlockBoard className="mt-auto" />
      </div>

      {/* Password Change Modal - Required when mustResetPassword is true */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => {
          if (!user?.mustResetPassword) {
            setShowPasswordModal(false);
          }
        }}
        isRequired={user?.mustResetPassword || false}
      />
    </div>
  );
};

export default Layout; 